/**
 * EmojiAutocompleteField
 *
 * A drop-in wrapper that adds emoji support to any text input or textarea:
 *   - Type `:shortcode` (e.g. `:smile`) → suggestion popover with arrow-key
 *     navigation + Enter / Tab to insert
 *   - Click the smiley button → full emoji picker
 *
 * Same UX pattern as `CampaignModal` — extracted here so we don't have to
 * re-implement ~100 lines of state per text field.
 *
 * Renders the project's `<Input>` (single-line) or `<Textarea>` (multi-line)
 * underneath, so all branding (focus styles, sizes, validation) is preserved.
 */

import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { EmojiClickData } from 'emoji-picker-react';
import { EmojiPickerCompact } from './EmojiPickerCompact';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Icon } from './Icon';
import { ICONS } from '@/constants/icons';
import { searchEmojis } from '@/utils/emojiAutocomplete';
import { cn } from '@/utils/cn';

interface BaseProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Optional id used for label binding */
  id?: string;
  /** Class to merge into the wrapper div (relative positioning required) */
  className?: string;
  /** Forwarded onKeyDown — invoked AFTER autocomplete navigation handling */
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** aria-describedby pass-through for character counters etc. */
  'aria-describedby'?: string;
  /** Padding on right for emoji button (default true) */
  showEmojiButton?: boolean;
}

interface InputProps extends BaseProps {
  multiline?: false;
  /** Max chars (single-line) */
  maxLength?: number;
  /** Optional inline element rendered AFTER the input (e.g. character counter) */
  trailingSlot?: React.ReactNode;
}

interface TextareaProps extends BaseProps {
  multiline: true;
  rows?: number;
  textareaClassName?: string;
}

export type EmojiAutocompleteFieldProps = InputProps | TextareaProps;

export const EmojiAutocompleteField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  EmojiAutocompleteFieldProps
>(function EmojiAutocompleteField(props, externalRef) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  useImperativeHandle(externalRef, () => inputRef.current!, []);

  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiQuery, setEmojiQuery] = useState('');
  const [emojiSuggestions, setEmojiSuggestions] = useState<
    Array<{ shortcode: string; native: string }>
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);

  const showEmojiButton = props.showEmojiButton !== false;
  // Same condition that renders the suggestion list; exposed as aria-expanded so a surrounding Modal keeps Esc for it.
  const suggestionsOpen = showSuggestions && emojiSuggestions.length > 0;
  const listId = useId();
  const activeOptionId = suggestionsOpen ? `${listId}-opt-${selectedSuggestionIndex}` : undefined;

  // Search for emoji suggestions when query changes
  useEffect(() => {
    if (emojiQuery.length >= 2) {
      const matches = searchEmojis(emojiQuery);
      setEmojiSuggestions(matches);
      setShowSuggestions(matches.length > 0);
      setSelectedSuggestionIndex(0);
    } else {
      setEmojiSuggestions([]);
      setShowSuggestions(false);
    }
  }, [emojiQuery]);

  // Scroll selected suggestion into view when navigating with keyboard
  useEffect(() => {
    if (!showSuggestions || !suggestionsRef.current) return;
    const selected = suggestionsRef.current.children[selectedSuggestionIndex] as HTMLElement;
    if (selected) selected.scrollIntoView({ block: 'nearest' });
  }, [selectedSuggestionIndex, showSuggestions]);

  // Detect :shortcode pattern as user types
  const handleChange = (next: string) => {
    props.onChange(next);
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? next.length;
    const before = next.slice(0, cursor);
    const match = before.match(/:([a-zA-Z0-9_+-]{2,})$/);
    if (match) {
      setEmojiQuery(match[1]);
    } else {
      setEmojiQuery('');
      setShowSuggestions(false);
    }
  };

  // Insert suggestion: replace `:query` with native emoji
  const insertSuggestion = (emoji: { shortcode: string; native: string }) => {
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? props.value.length;
    const before = props.value.slice(0, cursor);
    const after = props.value.slice(cursor);
    const colonIndex = before.lastIndexOf(':');
    if (colonIndex === -1) return;
    const next = before.slice(0, colonIndex) + emoji.native + after;
    props.onChange(next);
    setShowSuggestions(false);
    setEmojiQuery('');
    setTimeout(() => {
      const newPos = colonIndex + emoji.native.length;
      el.selectionStart = el.selectionEnd = newPos;
      el.focus();
    }, 0);
  };

  // Insert emoji at cursor (from picker click)
  const handleEmojiClick = (data: EmojiClickData) => {
    const el = inputRef.current;
    const emoji = data.emoji;
    if (el) {
      const start = el.selectionStart ?? props.value.length;
      const end = el.selectionEnd ?? props.value.length;
      const next = props.value.slice(0, start) + emoji + props.value.slice(end);
      props.onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        const newCursor = start + emoji.length;
        el.setSelectionRange(newCursor, newCursor);
      });
    } else {
      props.onChange(props.value + emoji);
    }
    setEmojiOpen(false);
  };

  // Keyboard navigation: arrows / enter / tab / esc when suggestions open
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (showSuggestions && emojiSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < emojiSuggestions.length - 1 ? prev + 1 : 0,
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : emojiSuggestions.length - 1,
        );
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertSuggestion(emojiSuggestions[selectedSuggestionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      }
    }
    props.onKeyDown?.(e);
  };

  // Picker button positioning: textareas place it top-right, inputs centred-right
  const buttonStyle: CSSProperties = props.multiline
    ? { position: 'absolute', top: '6px', right: '6px' }
    : {};

  // Suggestions popover
  const suggestionsPopover = suggestionsOpen && (
    <div
      ref={suggestionsRef}
      id={listId}
      role="listbox"
      className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto min-w-[200px]"
    >
      {emojiSuggestions.map((emoji, index) => (
        <button
          key={emoji.shortcode}
          id={`${listId}-opt-${index}`}
          role="option"
          aria-selected={index === selectedSuggestionIndex}
          tabIndex={-1}
          type="button"
          onClick={() => insertSuggestion(emoji)}
          className={cn(
            'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
            index === selectedSuggestionIndex && 'bg-primary-50',
          )}
        >
          <span className="text-lg">{emoji.native}</span>
          <span className="text-sm text-gray-600">:{emoji.shortcode}:</span>
        </button>
      ))}
    </div>
  );

  // Emoji picker trigger
  const pickerTrigger = showEmojiButton && (
    <Popover.Root open={emojiOpen} onOpenChange={setEmojiOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={props.disabled}
          style={buttonStyle}
          className={cn(
            !props.multiline &&
              'absolute right-1.5 top-1/2 -translate-y-1/2',
            'h-7 w-7 flex items-center justify-center rounded',
            'text-gray-400 hover:text-gray-600 transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
          aria-label="Insert emoji"
        >
          <Icon name={ICONS.EMO_SMILE} size="sm" ariaHidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="shadow-xl rounded-lg overflow-hidden bg-white z-[100000]"
          side="bottom"
          align="end"
          sideOffset={6}
          avoidCollisions
          collisionPadding={16}
        >
          <EmojiPickerCompact onEmojiClick={handleEmojiClick} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );

  // Input variant
  if (!props.multiline) {
    const trailingSlot = (props as InputProps).trailingSlot;
    // Input gets right-padding for the picker button + extra for trailing slot
    const inputPaddingClass = trailingSlot ? 'pr-20' : showEmojiButton ? 'pr-9' : '';
    return (
      <div className={cn('relative', props.className)}>
        <Input
          ref={inputRef as React.Ref<HTMLInputElement>}
          id={props.id}
          type="text"
          value={props.value}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown as React.KeyboardEventHandler<HTMLInputElement>}
          disabled={props.disabled}
          placeholder={props.placeholder}
          maxLength={(props as InputProps).maxLength}
          aria-describedby={props['aria-describedby']}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={suggestionsOpen}
          aria-controls={suggestionsOpen ? listId : undefined}
          aria-activedescendant={activeOptionId}
          className={inputPaddingClass}
        />
        {trailingSlot && (
          <span
            className={cn(
              'absolute top-1/2 -translate-y-1/2',
              showEmojiButton ? 'right-10' : 'right-3',
              'pointer-events-none',
            )}
          >
            {trailingSlot}
          </span>
        )}
        {pickerTrigger}
        {suggestionsPopover}
      </div>
    );
  }

  // Textarea variant
  return (
    <div className={cn('relative', props.className)}>
      <Textarea
        ref={inputRef as React.Ref<HTMLTextAreaElement>}
        id={props.id}
        value={props.value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown as React.KeyboardEventHandler<HTMLTextAreaElement>}
        disabled={props.disabled}
        placeholder={props.placeholder}
        rows={(props as TextareaProps).rows}
        aria-describedby={props['aria-describedby']}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={suggestionsOpen}
          aria-controls={suggestionsOpen ? listId : undefined}
          aria-activedescendant={activeOptionId}
        className={cn(
          (props as TextareaProps).textareaClassName,
          showEmojiButton && 'pr-10',
        )}
      />
      {pickerTrigger}
      {suggestionsPopover}
    </div>
  );
});

export default EmojiAutocompleteField;
